import {ModalToolContainerService, WebmateToolConfig} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {TOOL_CONTAINER_DATA} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component';
import {
    faAlignLeft,
    faArrowDown,
    faArrowLeft,
    faArrowUp,
    faChartPie,
    faChevronDown,
    faChevronRight,
    faEdit,
    faEye,
    faGlobe,
    faImage,
    faPlus,
    faQuestionCircle,
    faSave,
    faTimes,
    faTrash
} from '@fortawesome/pro-solid-svg-icons';
import {switchMap} from 'rxjs/operators';
import {FormsModule, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {FormsCommonsModule} from '@webmate-web-core/shared/components/forms-commons/forms-commons.module';

import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {ReportService} from '@webmate-web-core/shared/services/report.service';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {ButtonModule} from 'primeng/button';

import {
    PreviewReportToolComponent,
    PreviewReportToolConfig
} from '@webmate-web-core/projects/project/auditlab/audits/preview-report-tool.component';
import {animate, style, transition, trigger} from '@angular/animations';
import {TooltipModule} from 'primeng/tooltip';
import {BehaviorSubject} from 'rxjs';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {InputTextModule} from 'primeng/inputtext';
import {computeSectionNumberingHelper} from '@webmate-web-core/shared/types/reports';
import {ReportSectionContentComponent} from '@webmate-web-core/shared/components/report/report-section-content.component';
import {
    DefaultNumberingScheme,
    Report,
    ReportId,
    ReportSection,
    ReportSectionContent,
    ReportSectionId
} from '../../../../../report/report_types';
import {plusDown, plusRightDown, plusUp} from '@webmate-web-core/shared/icons';
import {getRandomUUID} from '@webmate-web-core/shared/utils/utils';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {NgStyle, NgTemplateOutlet} from '@angular/common';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';

export class EditReportToolConfig implements WebmateToolConfig {
    scrollable = true;

    constructor(public toolName: string = 'Edit Report', public reportId: ReportId, public projectId: ProjectId, public onCreateReport: (reportId: ReportId) => void) {}
}

@Component({
    selector: 'wm-edit-preview-report-tool',
    templateUrl: './edit-report-tool.component.html',
    standalone: true,
    styleUrls: ['./edit-report-tool.component.less'],
    imports: [FormsModule, ReactiveFormsModule, FormsCommonsModule, FontAwesomeModule, ButtonModule, TooltipModule, ProgressSpinnerModule, InputTextModule, ReportSectionContentComponent, Tabs, TabPanel, TabList, Tab, TabPanels, NgTemplateOutlet, NgStyle],
    animations: [
        trigger('sectionActionsAnimation',
            [
                transition(':enter', [
                    style({opacity: 0, transform: 'scaleY(0.8)'}),
                    animate('{{showTransitionParams}}')
                ]),
                transition(':leave', [
                    animate('{{hideTransitionParams}}', style({ opacity: 0 }))
                  ])
            ]
        )
    ]
})
export class EditReportToolComponent implements OnInit {

    @ViewChild('sectionsContainer', {static: false}) sectionsContainer: ElementRef;

    form: UntypedFormGroup;

    reportId: ReportId;

    report?: Report;

    loadingReport = false;

    sectionIsExpanded: {[sectionId: string]: boolean} = {};
    sectionIconMap: {[sectionId: string]: IconDefinition } = {
        'TextContent': faAlignLeft,
        'ChartContent': faChartPie,
        'ImageContent': faImage,
    };

    editedSectionTitle: ReportSectionId | null = null;
    editedSectionTitleContent: string = '';
    editedSectionTitleOriginal: string = '';

    disableSectionActions: boolean = false;
    currentSectionAction = {
        currentSectionId: '',
        visible: false,
        right: 0,
        top: 0,
        isFirst: false,
        isLast: false,
    };

    sectionNumbers: {[sectionId: string]: string} = {};

    contentNeedsUpdate$ = new BehaviorSubject<void>(undefined);
    allowCustomStyling: boolean = false;

    constructor(@Inject(TOOL_CONTAINER_DATA) private config: EditReportToolConfig,
                private reportService: ReportService,
                private toolService: ModalToolContainerService,
                private featureService: FeatureService) {
        this.reportId = this.config.reportId;
    }

    ngOnInit() {
        this.featureService.getFeature("webmate.report-customization").subscribe(feature => this.allowCustomStyling = feature?.isActive || false);

        this.loadingReport = true;
        this.contentNeedsUpdate$.pipe(
            switchMap(_ => this.reportService.getReport$(this.reportId))
        ).subscribe({
            next: report => {
                this.report = report;
                this.loadingReport = false;
                this.computeSectionNumbers();
            },
            error: err => {
                console.error('Error loading report', err);
                this.loadingReport = false;
            }
        });
    }

    hideSectionActions() {
        this.currentSectionAction.visible = false;
        this.currentSectionAction.currentSectionId = '';
    }

    sectionHovered(section: ReportSection, event: MouseEvent) {
        if (event.target === null) {
            this.currentSectionAction.visible = false;
        } else {
            this.currentSectionAction.currentSectionId = section.id;

            const target = event.target as HTMLElement;
            const containerElement = this.sectionsContainer.nativeElement as HTMLElement;
            const containerRect = containerElement.getBoundingClientRect();

            this.currentSectionAction.visible = true;
            this.currentSectionAction.right = target.getBoundingClientRect().x - containerRect.x + target.getBoundingClientRect().width;// + EditReportToolComponent.SectionActionsOffset;
            this.currentSectionAction.top = target.offsetTop - (100 /* height of actions box */ / 2) + target.getBoundingClientRect().height / 2 /* center with target */;

            this.currentSectionAction.isFirst = section.id === this.report?.sections[0].id;
            this.currentSectionAction.isLast = section.id === this.report?.sections[this.report.sections.length - 1].id;

        }
    }

    editSectionTitle(sectionId: ReportSectionId, originalTitle: string) {
        this.editedSectionTitle = sectionId;
        this.editedSectionTitleContent = originalTitle;
        this.editedSectionTitleOriginal = originalTitle;
        this.disableSectionActions = true;
    }

    cancelSaveSectionTitle() {
        this.editedSectionTitle = null;
        this.editedSectionTitleContent = '';
        this.editedSectionTitleOriginal = '';
        this.disableSectionActions = false;
    }

    saveSectionTitle(sectionId: ReportSectionId) {
        if (!this.editedSectionTitleContent || this.editedSectionTitleContent === this.editedSectionTitleOriginal) {
            this.editedSectionTitle = null;
            this.editedSectionTitleContent = '';
            return;
        }
        this.reportService.updateReport(this.reportId, {
            type: 'SetSectionTitle',
            sectionId: sectionId,
            title: this.editedSectionTitleContent
        }).subscribe({
            next: _ => {
                this.editedSectionTitle = null;
                this.disableSectionActions = false;
                this.contentNeedsUpdate$.next();
            }, error: err => {
                console.error('Error updating section title', err);
                this.disableSectionActions = false;
            }
        });
    }

    findNextSection(parentSection: ReportSectionId | null, sections: ReportSection[], sectionId: ReportSectionId): [ReportSectionId | null, ReportSectionId | null] {
        // find the section following the given section
        let found = false;
        for (const section of sections) {
            if (found) {
                return [section.id, parentSection];
            }
            if (section.id === sectionId) {
                found = true;
            }
            if (section.subsections.length > 0) {
                const [next, parent] = this.findNextSection(section.id, section.subsections, sectionId);
                if (next) {
                    return [next, parent];
                }
            }
        }
        return [null, null];
    }

    // Move section one position up (not hierarchical)
    moveSectionUp(section: ReportSectionId) {
        this.reportService.updateReport(this.reportId, {
            type: 'MoveSectionUp',
            sectionId: section,
        }).subscribe(_ => {
            this.hideSectionActions();
            this.contentNeedsUpdate$.next();
        });

    }

    insertSectionBefore(section: ReportSectionId) {
        this.reportService.updateReport(this.reportId, {
            type: 'InsertSection',
            section: {
                sectionInfo: {
                    id: getRandomUUID(),
                    subsections: [],
                    styleClasses: [],
                },
                content: {},
                blobs: []
            },
            before: section,
        }).subscribe(_ => {
            this.hideSectionActions();
            this.contentNeedsUpdate$.next();
        });
    }

    insertSectionAfter(section: ReportSectionId) {
        const [nextSection, _] = this.findNextSection(null, this.report!.sections, section);

        if (!nextSection) {
            return;
        }

        this.reportService.updateReport(this.reportId, {
            type: 'InsertSection',
            section: {
                sectionInfo: {
                    id: getRandomUUID(),
                    subsections: [],
                    styleClasses: [],
                },
                content: {},
                blobs: []
            },
            before: nextSection,
        }).subscribe(_ => {
            this.hideSectionActions();
            this.contentNeedsUpdate$.next();
        });

    }

    addSubSection(section: ReportSectionId) {
        this.reportService.updateReport(this.reportId, {
            type: 'AppendSection',
            sectionWithContent: {
                sectionInfo: {
                    id: getRandomUUID(),
                    subsections: [],
                    styleClasses: [],
                },
                content: {},
                blobs: []
            },
            optParent: section,
        }).subscribe(_ => {
            this.hideSectionActions();
            this.sectionIsExpanded[section] = true;
            this.contentNeedsUpdate$.next();
        });
    }

    moveSectionDown(section: ReportSectionId) {
        this.reportService.updateReport(this.reportId, {
            type: 'MoveSectionDown',
            sectionId: section,
        }).subscribe(_ => {
            this.hideSectionActions();
            this.contentNeedsUpdate$.next();
        });
    }

    deleteSection(section: ReportSectionId) {
        this.reportService.updateReport(this.reportId, {
            type: 'DeleteSection',
            sectionId: section,
        }).subscribe(_ => {
            this.hideSectionActions();
            this.contentNeedsUpdate$.next();
        });
    }

    moveSectionHigher(section: ReportSectionId) {
        this.reportService.updateReport(this.reportId, {
            type: 'MoveSectionHigher',
            sectionId: section,
        }).subscribe(_ => {
            this.hideSectionActions();
            this.contentNeedsUpdate$.next();
        });

    }

    computeSectionNumbers() {
        if (!this.report) {
            return;
        }
        const documentNumbering = this.report.renderConfig.numberingScheme || DefaultNumberingScheme;
        computeSectionNumberingHelper(this.sectionNumbers, '', 0, this.report.sections, documentNumbering);
    }

    toggleSection(sectionId: string) {
        this.sectionIsExpanded[sectionId] = !this.sectionIsExpanded[sectionId];
    }


    showPreview() {
        this.toolService.openTool(PreviewReportToolComponent, new PreviewReportToolConfig(this.reportId));
    }

    updateContent(sectionId: ReportSectionId, contentIdx: number, newContent: ReportSectionContent) {
        // replace the content in the content array of section
        // If there is no content, we create a new array.
        const sectionContent = this.report!.sectionContent[sectionId] || [];

        const newContentArray = sectionContent.slice(0, contentIdx);
        newContentArray[contentIdx] = newContent;
        newContentArray.push(...sectionContent.slice(contentIdx + 1));

        this.reportService.updateReport(this.reportId, {
            type: 'SetSectionContent',
            sectionId: sectionId,
            content: newContentArray
        }).subscribe({
            next: _ => {
                this.contentNeedsUpdate$.next();
            },
            error: err => {
                console.error('Error updating content', err);
            }
        });
    }

    saveStyling(cssText: string) {
        const config = this.report?.renderConfig;
        if (!config) {
            return;
        }
        const profile = config.renderingProfiles['default'];
        if (!config.renderingProfiles['default']) {
            config.renderingProfiles['default'] = {
                name: 'default',
                styling: {
                    cssText: '',
                }
            };
        }
        config.renderingProfiles['default'].styling = { ...(profile?.styling || {}), cssText };
        this.saveRenderProfile();
    }

    saveRenderProfile() {
        console.debug('Saving render profile', this.report!.renderConfig);
        this.reportService.updateReport(this.reportId, {
            type: 'SetRenderConfig',
            renderConfig: this.report!.renderConfig
        }).subscribe({
            next: _ => {
                this.contentNeedsUpdate$.next();
            },
            error: err => {
                console.error('Error updating render config', err);
            }
        });
    }

    protected readonly faPlus = faPlus;
    protected readonly faEye = faEye;
    protected readonly faGlobe = faGlobe;
    protected readonly faSave = faSave;
    protected readonly faChevronDown = faChevronDown;
    protected readonly faQuestionCircle = faQuestionCircle;
    protected readonly faChevronRight = faChevronRight;
    protected readonly faTrash = faTrash;
    protected readonly faEdit = faEdit;
    protected readonly faArrowLeft = faArrowLeft;
    protected readonly faArrowUp = faArrowUp;
    protected readonly faArrowDown = faArrowDown;
    protected readonly faTimes = faTimes;
    protected readonly plusDown = plusDown;
    protected readonly plusUp = plusUp;
    protected readonly plusRightDown = plusRightDown;
}
